import { useEffect, useRef, useState } from 'react';
import { FiEdit2, FiPlus, FiTrash2, FiUser } from 'react-icons/fi';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';

import { useVhAdminStore, VH_ADMIN_PAGE_SIZE } from '@/admin/admin.store';
import type {
  VhAdminUser,
  VhAdminWhitelistAction,
  VhAdminWhitelistEntry,
  VhAdminWhitelistPayload,
} from '@/admin/admin.types';
import { AdminButton } from '@/admin/components/AdminButton';
import { AdminCard, AdminCardList } from '@/admin/components/AdminCard';
import { AdminModal } from '@/admin/components/AdminModal';
import { AdminPagination } from '@/admin/components/AdminPagination';
import { AdminSearchInput } from '@/admin/components/AdminSearchInput';
import { AdminSpinner } from '@/admin/components/AdminSpinner';
import {
  AdminTable,
  AdminTableCell,
  AdminTableHead,
  AdminTableHeadCell,
  AdminTableRow,
} from '@/admin/components/AdminTable';
import { AdminToggle } from '@/admin/components/AdminToggle';
import { UserDetailsPanel } from '@/admin/pages/UserDetailsPanel';
import styles from '@/admin/pages/WhitelistPage.module.scss';
import { type ApiException } from '@/common/error';
import { formatDate } from '@/utils/formatDate';
import { toast } from '@/utils/toast';

const NAME_MAX_LENGTH = 255;
const DESCRIPTION_MAX_LENGTH = 1000;

const ACTION_LABELS: Record<VhAdminWhitelistAction, string> = {
  enabled: 'Turned on gated access',
  disabled: 'Turned off gated access',
  added: 'Added a contact',
  updated: 'Edited a contact',
  removed: 'Removed a contact',
};

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/** The backend lower-cases emails and formats phones as E.164; this only catches what it would refuse. */
const isContact = (value: string) =>
  z.string().email().safeParse(value).success || isValidPhoneNumber(value);

const optional = (value: string) => value.trim() || undefined;

/**
 * Who made a change. The id turns null only when that admin's account is deleted: seeded rows
 * name the system user, which people resolves to "VesselHaven System".
 */
const actorName = (id: string | null, people: Record<string, string>) =>
  id ? (people[id] ?? id) : 'Deleted admin';

type EntryFormModalProps = {
  entry: VhAdminWhitelistEntry | null;
  onClose: () => void;
  onSubmit: (payload: VhAdminWhitelistPayload) => Promise<void>;
};

const EntryFormModal = ({ entry, onClose, onSubmit }: EntryFormModalProps) => {
  const [contact, setContact] = useState(entry?.contact ?? '');
  const [name, setName] = useState(entry?.name ?? '');
  const [description, setDescription] = useState(entry?.description ?? '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const trimmed = contact.trim();
    if (!trimmed) {
      setError('Please enter an email or mobile number.');
      return;
    }
    if (!isContact(trimmed)) {
      setError('Please enter a valid email, or a mobile number with its country code.');
      return;
    }

    setError('');
    setSaving(true);
    try {
      await onSubmit({
        contact: trimmed,
        name: optional(name),
        description: optional(description),
      });
      onClose();
    } catch (err) {
      console.error(err);
      if ((err as ApiException | undefined)?.type === 'DUPLICATE') {
        setError('This contact is already on the access list.');
        return;
      }
      setError(
        entry
          ? 'Couldn’t save the contact. Please check the details and try again.'
          : 'Couldn’t add the contact. Please check the details and try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal
      title={entry ? `Edit ${entry.contact}` : 'Add Contact'}
      onClose={onClose}
      footer={
        <>
          <AdminButton onClick={onClose} disabled={saving}>
            Cancel
          </AdminButton>
          <AdminButton tone="primary" onClick={submit} disabled={saving}>
            {entry ? 'Save Contact' : 'Add Contact'}
          </AdminButton>
        </>
      }
    >
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Email or Mobile Number</span>
        <input
          autoFocus
          className={styles.input}
          value={contact}
          placeholder="jane.doe@example.com"
          onChange={(event) => setContact(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && submit()}
        />
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Name (optional)</span>
        <input
          className={styles.input}
          value={name}
          maxLength={NAME_MAX_LENGTH}
          placeholder="Jane Doe"
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && submit()}
        />
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Description (optional)</span>
        <textarea
          className={styles.input}
          value={description}
          maxLength={DESCRIPTION_MAX_LENGTH}
          rows={3}
          placeholder="Captain of M/Y Aurora, testing crew rosters"
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>

      {error && <p className={styles.error}>{error}</p>}
    </AdminModal>
  );
};

/**
 * The entry's own name, else the linked person's name from their profile (muted, and never saved
 * to the entry, so it follows a rename).
 */
const EntryName = ({ entry, hideEmpty }: { entry: VhAdminWhitelistEntry; hideEmpty?: boolean }) => {
  const people = useVhAdminStore((state) => state.whitelistPeople);

  if (entry.name) return <div>{entry.name}</div>;

  const profileName = entry.user_id ? people[entry.user_id] : undefined;
  // resolveNames falls back to the id for a person who has no name.
  if (profileName && profileName !== entry.user_id) {
    return (
      <div className={styles.muted} title="From their profile">
        {profileName}
      </div>
    );
  }

  return hideEmpty ? null : <div className={styles.muted}>-</div>;
};

const AccountLink = ({
  entry,
  onView,
}: {
  entry: VhAdminWhitelistEntry;
  onView: (userId: string) => void;
}) => {
  const userId = entry.user_id;
  if (!userId) return <span className={styles.muted}>No account yet</span>;

  return (
    <AdminButton
      size="sm"
      onClick={() => onView(userId)}
      aria-label={`View the user ${entry.contact}`}
    >
      <FiUser />
      View User
    </AdminButton>
  );
};

const EntryActions = ({
  entry,
  onEdit,
  onRemove,
}: {
  entry: VhAdminWhitelistEntry;
  onEdit: () => void;
  onRemove: () => void;
}) => (
  <div className={styles.actions}>
    <AdminButton size="sm" onClick={onEdit} aria-label={`Edit ${entry.contact}`}>
      <FiEdit2 />
      Edit
    </AdminButton>
    <AdminButton size="sm" tone="danger" onClick={onRemove} aria-label={`Remove ${entry.contact}`}>
      <FiTrash2 />
      Remove
    </AdminButton>
  </div>
);

const GatedAccess = () => {
  const settings = useVhAdminStore((state) => state.whitelistSettings);
  const people = useVhAdminStore((state) => state.whitelistPeople);
  const setWhitelistEnabled = useVhAdminStore((state) => state.setWhitelistEnabled);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const apply = async (enabled: boolean) => {
    setSaving(true);
    try {
      await setWhitelistEnabled(enabled);
      setConfirming(false);
    } catch (err) {
      console.error(err);
      toast.error(
        enabled
          ? 'Couldn’t turn on gated access. Please try again.'
          : 'Couldn’t turn off gated access. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  // Turning it on signs people out, so it is confirmed; turning it off only lets people in.
  const change = (enabled: boolean) => (enabled ? setConfirming(true) : apply(false));

  if (!settings) {
    return (
      <div className={styles.gate}>
        <AdminSpinner />
      </div>
    );
  }

  const changedBy = settings.updated_by
    ? (people[settings.updated_by] ?? settings.updated_by)
    : null;

  return (
    <div className={styles.gate}>
      <AdminToggle
        checked={settings.enabled}
        onChange={change}
        disabled={saving}
        label="Gated access"
      />
      <div>
        <div className={styles.gateTitle}>Gated access is {settings.enabled ? 'on' : 'off'}</div>
        <div className={styles.muted}>
          {settings.enabled
            ? 'Only contacts on this list and admins can sign in or sign up.'
            : 'Anyone can sign in or sign up.'}
        </div>
        {changedBy && (
          <div className={styles.muted}>
            Changed by {changedBy} on {formatDateTime(settings.updated_at)}.
          </div>
        )}
      </div>

      {confirming && (
        <AdminModal
          title="Turn on gated access?"
          onClose={() => setConfirming(false)}
          footer={
            <>
              <AdminButton onClick={() => setConfirming(false)} disabled={saving}>
                Cancel
              </AdminButton>
              <AdminButton tone="primary" onClick={() => apply(true)} disabled={saving}>
                Turn On Gated Access
              </AdminButton>
            </>
          }
        >
          <p>
            Anyone not on this list, other than admins, is signed out within minutes and can’t sign
            in or sign up.
          </p>
        </AdminModal>
      )}
    </div>
  );
};

const History = () => {
  const {
    whitelistAudit,
    whitelistAuditCount,
    whitelistAuditPage,
    whitelistAuditLoading,
    whitelistPeople,
    fetchWhitelistAudit,
  } = useVhAdminStore();

  const actorOf = (actorId: string | null) => actorName(actorId, whitelistPeople);

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>History</h2>

      {whitelistAuditLoading && whitelistAudit.length === 0 ? (
        <div className={styles.loading}>
          <AdminSpinner />
        </div>
      ) : whitelistAudit.length === 0 ? (
        <div className={styles.empty}>Changes to the access list will show here.</div>
      ) : (
        <>
          <div className={styles.desktopOnly}>
            <AdminTable>
              <AdminTableHead>
                <AdminTableHeadCell>When</AdminTableHeadCell>
                <AdminTableHeadCell>Change</AdminTableHeadCell>
                <AdminTableHeadCell>Contact</AdminTableHeadCell>
                <AdminTableHeadCell>By</AdminTableHeadCell>
              </AdminTableHead>
              <tbody>
                {whitelistAudit.map((record) => (
                  <AdminTableRow key={record.id}>
                    <AdminTableCell>
                      <span className={styles.muted}>{formatDateTime(record.created_at)}</span>
                    </AdminTableCell>
                    <AdminTableCell>{ACTION_LABELS[record.action]}</AdminTableCell>
                    <AdminTableCell>{record.contact ?? '-'}</AdminTableCell>
                    <AdminTableCell>{actorOf(record.actor_id)}</AdminTableCell>
                  </AdminTableRow>
                ))}
              </tbody>
            </AdminTable>
          </div>

          <div className={styles.mobileOnly}>
            <AdminCardList>
              {whitelistAudit.map((record) => (
                <AdminCard key={record.id}>
                  <div className={styles.contact}>
                    {ACTION_LABELS[record.action]}
                    {record.contact && ` · ${record.contact}`}
                  </div>
                  <div className={styles.cardMeta}>
                    {actorOf(record.actor_id)} · {formatDateTime(record.created_at)}
                  </div>
                </AdminCard>
              ))}
            </AdminCardList>
          </div>

          <AdminPagination
            page={whitelistAuditPage}
            pageSize={VH_ADMIN_PAGE_SIZE}
            total={whitelistAuditCount}
            onPageChange={(page) => fetchWhitelistAudit(page)}
          />
        </>
      )}
    </section>
  );
};

export const WhitelistPage = () => {
  const {
    whitelist,
    whitelistCount,
    whitelistPage,
    whitelistLoading,
    whitelistPeople,
    whitelistSettings,
    fetchWhitelist,
    fetchWhitelistSettings,
    fetchWhitelistAudit,
    createWhitelistEntry,
    updateWhitelistEntry,
    deleteWhitelistEntry,
    fetchAccountWhitelist,
    fetchUserById,
    audiences,
    fetchAudiences,
  } = useVhAdminStore();

  // The account opened from an entry, shown in the same side panel as All Users.
  const [viewedUser, setViewedUser] = useState<VhAdminUser | null>(null);

  const viewUser = async (userId: string) => {
    // The panel names the person's audience, which this page doesn't load otherwise.
    if (audiences.length === 0) {
      fetchAudiences().catch((err) => console.error(err));
    }

    try {
      const user = await fetchUserById(userId);
      if (!user) {
        toast.error('Couldn’t find this user. Please check All Users.');
        return;
      }
      setViewedUser(user);
    } catch (err) {
      console.error(err);
      toast.error('Couldn’t open the user. Please try again.');
    }
  };

  const [search, setSearch] = useState('');
  /** `null` while closed, `{ entry: null }` for an add, `{ entry }` for an edit. */
  const [form, setForm] = useState<{ entry: VhAdminWhitelistEntry | null } | null>(null);
  /** The entry to remove, and the other contacts of its account that sign in to it too. */
  const [pendingRemove, setPendingRemove] = useState<{
    entry: VhAdminWhitelistEntry;
    siblings: VhAdminWhitelistEntry[];
  } | null>(null);
  const [removing, setRemoving] = useState(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    fetchWhitelistSettings().catch((err) => {
      console.error(err);
      toast.error('Couldn’t load gated access. Please refresh the page.');
    });
    fetchWhitelistAudit(1).catch((err) => {
      console.error(err);
      toast.error('Couldn’t load the history. Please refresh the page.');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const load = () =>
      fetchWhitelist({ page: 1, search }).catch((err) => {
        console.error(err);
        toast.error('Couldn’t load the access list. Please refresh.');
      });

    if (isFirstRender.current) {
      isFirstRender.current = false;
      load();
      return;
    }

    const timeout = setTimeout(load, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const creatorOf = (entry: VhAdminWhitelistEntry) => actorName(entry.created_by, whitelistPeople);

  const submitForm = async (payload: VhAdminWhitelistPayload) => {
    if (form?.entry) {
      await updateWhitelistEntry(form.entry.id, payload);
      toast.success('The contact’s details have been updated.');
    } else {
      await createWhitelistEntry(payload);
      toast.success('Your contact has been added.');
    }
  };

  // An account's email and phone both sign in to it, so the admin picks one or all of them.
  const askRemove = async (entry: VhAdminWhitelistEntry) => {
    if (!entry.user_id) {
      setPendingRemove({ entry, siblings: [] });
      return;
    }

    try {
      const entries = await fetchAccountWhitelist(entry.user_id);
      setPendingRemove({ entry, siblings: entries.filter((other) => other.id !== entry.id) });
    } catch (err) {
      console.error(err);
      toast.error('Couldn’t check this account’s contacts. Please try again.');
    }
  };

  const confirmRemove = async (wholeAccount: boolean) => {
    if (!pendingRemove) return;

    setRemoving(true);
    try {
      await deleteWhitelistEntry(pendingRemove.entry.id, wholeAccount);
      toast.success(
        wholeAccount
          ? 'The account’s contacts have been removed.'
          : 'The contact has been removed from the list.',
      );
      setPendingRemove(null);
    } catch (err) {
      console.error(err);
      toast.error('Couldn’t remove the contact. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  const openEdit = (entry: VhAdminWhitelistEntry) => setForm({ entry });

  return (
    <div>
      <div className={styles.header}>
        <h1 className={styles.title}>Access List</h1>
      </div>

      <p className={styles.hint}>
        While gated access is on, only these contacts and admins can sign in or sign up.
      </p>

      <GatedAccess />

      <div className={styles.toolbar}>
        <h2 className={styles.sectionTitle}>Contacts</h2>
        <div className={styles.toolbarActions}>
          <AdminSearchInput value={search} onChange={setSearch} placeholder="Search contacts" />
          <AdminButton tone="primary" onClick={() => setForm({ entry: null })}>
            <FiPlus />
            Add Contact
          </AdminButton>
        </div>
      </div>

      {whitelistLoading && whitelist.length === 0 ? (
        <div className={styles.loading}>
          <AdminSpinner />
        </div>
      ) : whitelist.length === 0 ? (
        <div className={styles.empty}>
          {search ? (
            'No contacts match. Please try a different search.'
          ) : (
            <>
              <p>Please add a contact to get started.</p>
              <AdminButton tone="primary" onClick={() => setForm({ entry: null })}>
                <FiPlus />
                Add Contact
              </AdminButton>
            </>
          )}
        </div>
      ) : (
        <>
          <div className={styles.desktopOnly}>
            <AdminTable>
              <AdminTableHead>
                <AdminTableHeadCell>Contact</AdminTableHeadCell>
                <AdminTableHeadCell>Name</AdminTableHeadCell>
                <AdminTableHeadCell>Description</AdminTableHeadCell>
                <AdminTableHeadCell>Added</AdminTableHeadCell>
                <AdminTableHeadCell>Account</AdminTableHeadCell>
                <AdminTableHeadCell>Actions</AdminTableHeadCell>
              </AdminTableHead>
              <tbody>
                {whitelist.map((entry) => (
                  <AdminTableRow key={entry.id}>
                    <AdminTableCell>
                      <span className={styles.contact}>{entry.contact}</span>
                    </AdminTableCell>
                    <AdminTableCell>
                      <EntryName entry={entry} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <span className={styles.description}>{entry.description ?? '-'}</span>
                    </AdminTableCell>
                    <AdminTableCell>
                      <span className={styles.muted}>
                        {formatDate(entry.created_at)} · {creatorOf(entry)}
                      </span>
                    </AdminTableCell>
                    <AdminTableCell>
                      <AccountLink entry={entry} onView={viewUser} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <EntryActions
                        entry={entry}
                        onEdit={() => openEdit(entry)}
                        onRemove={() => askRemove(entry)}
                      />
                    </AdminTableCell>
                  </AdminTableRow>
                ))}
              </tbody>
            </AdminTable>
          </div>

          <div className={styles.mobileOnly}>
            <AdminCardList>
              {whitelist.map((entry) => (
                <AdminCard key={entry.id}>
                  <div className={styles.cardHeader}>
                    <span className={styles.contact}>{entry.contact}</span>
                    <EntryActions
                      entry={entry}
                      onEdit={() => openEdit(entry)}
                      onRemove={() => askRemove(entry)}
                    />
                  </div>
                  <EntryName entry={entry} hideEmpty />
                  {entry.description && (
                    <div className={styles.description}>{entry.description}</div>
                  )}
                  <div className={styles.cardMeta}>
                    {formatDate(entry.created_at)} · {creatorOf(entry)}
                  </div>
                  <div className={styles.cardAccount}>
                    <AccountLink entry={entry} onView={viewUser} />
                  </div>
                </AdminCard>
              ))}
            </AdminCardList>
          </div>

          <AdminPagination
            page={whitelistPage}
            pageSize={VH_ADMIN_PAGE_SIZE}
            total={whitelistCount}
            onPageChange={(page) => fetchWhitelist({ page })}
          />
        </>
      )}

      <History />

      <UserDetailsPanel user={viewedUser} onClose={() => setViewedUser(null)} />

      {form && (
        <EntryFormModal entry={form.entry} onClose={() => setForm(null)} onSubmit={submitForm} />
      )}

      {pendingRemove && (
        <AdminModal
          title={`Remove “${pendingRemove.entry.contact}”?`}
          onClose={() => setPendingRemove(null)}
          footer={
            <>
              <AdminButton onClick={() => setPendingRemove(null)} disabled={removing}>
                Cancel
              </AdminButton>
              {pendingRemove.siblings.length > 0 && (
                <AdminButton tone="danger" onClick={() => confirmRemove(true)} disabled={removing}>
                  Remove All Contacts
                </AdminButton>
              )}
              <AdminButton tone="danger" onClick={() => confirmRemove(false)} disabled={removing}>
                {pendingRemove.siblings.length > 0 ? 'Remove This Contact' : 'Remove'}
              </AdminButton>
            </>
          }
        >
          {pendingRemove.siblings.length > 0 ? (
            <>
              <p>
                The same account also signs in with{' '}
                {pendingRemove.siblings.map((other) => other.contact).join(', ')}.
              </p>
              <p>
                Removing only this contact keeps the account’s access and sessions. Removing all{' '}
                {pendingRemove.siblings.length + 1}{' '}
                {whitelistSettings?.enabled
                  ? 'signs the account out and stops it from signing in.'
                  : 'stops it from signing in once gated access is on.'}
              </p>
            </>
          ) : (
            <p>
              {whitelistSettings?.enabled
                ? 'They’ll be signed out and won’t be able to sign in again.'
                : 'They won’t be able to sign in once gated access is on.'}
            </p>
          )}
        </AdminModal>
      )}
    </div>
  );
};
